import React, { useEffect } from "react";
import SeoRelatedLinks from "../../../components/seo/SeoRelatedLinks.jsx";
import SeoHeader from "../../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../../components/seo/SeoWorkSteps.jsx";

const sections = [
  {
    id: "start",
    label: "En pizzeria trenger en meny som viser varianter tydelig",
  },
  {
    id: "why",
    label: "Hvorfor gir en digital meny mening for pizzeriaer?",
  },
  {
    id: "structure",
    label: "Hvordan bør en digital meny for en pizzeria struktureres?",
  },
  {
    id: "sizes",
    label: "Hvordan bør pizzastørrelser, ekstra valg og topping vises?",
  },
  {
    id: "photos",
    label: "Bør en pizzeria bruke bilder i den digitale menyen?",
  },
  {
    id: "mistakes",
    label: "Hvilke feil skjer med digitale menyer for pizzeriaer?",
  },
  {
    id: "recommendation",
    label: "Hvordan ville jeg startet som pizzeria-eier?",
  },
  {
    id: "next-steps",
    label: "Hva bør du gjøre nå?",
  },
  {
    id: "bestplace",
    label: "Hvordan Best Place hjelper deg med å lage en digital pizzeria-meny",
  },
  {
    id: "faq",
    label: "FAQ om digitale menyer for pizzeriaer",
  },
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Visuell idé">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>
      <div>
        <strong>Idé til illustrasjon:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default function DigitalMenuPizzeriaNo() {
  useEffect(() => {
    document.documentElement.setAttribute("lang", "no");
    document.title =
      "Digital meny for pizzeriaer: pizzastørrelser, topping og tilbud online | Best Place";
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="no"
        langLinks={{
          de: "/de/digitale-speisekarte-pizzeria",
          en: "/en/digital-menu-pizzeria",
          no: "/no/digital-meny-pizzeria",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Digital meny for pizzeriaer: administrer pizzastørrelser, topping
              og tilbud online
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                En pizzeria har ofte flere varianter enn en vanlig
                restaurantmeny: ulike pizzastørrelser, ekstra topping,
                vegetariske alternativer, familiepizza, lunsjtilbud, pasta,
                salater, desserter og drikke. Derfor kan en digital meny være
                spesielt nyttig her.
              </p>

              <p className="seoHero__text">
                I stedet for å vedlikeholde flere PDF-filer, trykte flyere eller
                utdaterte menykort kan pizzeriaen oppdatere retter, priser,
                ekstra valg og tilbud online. QR-koden forblir den samme, mens
                menyen bak den holdes oppdatert.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="no" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Innhold i artikkelen</h2>

        <nav className="qrArticle__nav" aria-label="Artikkelnavigasjon">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="start" className="acquaintance">
          <h2>En pizzeria trenger en meny som viser varianter tydelig</h2>

          <p>
            For en pizzeria er det ofte ikke nok å legge en enkel liste med
            retter online. Gjester vil vite: hvilken størrelse finnes? Hvilken
            topping kan legges til? Finnes det vegetariske alternativer? Kan
            ingredienser endres? Hvilke tilbud gjelder i dag?
          </p>

          <p>
            En digital pizzeria-meny bør gjøre disse valgene lettere å forstå.
            Den bør hjelpe gjester med å finne pizza, pasta, drikke, tilbud,
            størrelser og ekstra valg uten å lese en overfylt PDF eller en
            utdatert flyer.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>Svakere</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Lang PDF-meny med små pristabeller.
                </li>
                <li className="text__seo-li">
                  Størrelser og ekstra valg er vanskelige å forstå.
                </li>
                <li className="text__seo-li">
                  Gamle flyere blir liggende online.
                </li>
                <li className="text__seo-li">
                  Utilgjengelige produkter vises fortsatt.
                </li>
                <li className="text__seo-li">
                  Lunsjtilbud blir ikke oppdatert.
                </li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Bedre</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Tydelige kategorier for pizza, pasta, salater og drikke.
                </li>
                <li className="text__seo-li">
                  Størrelser og ekstra valg vises tydelig.
                </li>
                <li className="text__seo-li">Priser oppdateres online.</li>
                <li className="text__seo-li">
                  Utilgjengelige retter kan skjules.
                </li>
                <li className="text__seo-li">QR-koden forblir den samme.</li>
              </ul>
            </div>
          </div>

          <h3>Start med en liten digital pizzameny</h3>

          <p>
            Legg først inn de mest populære pizzaene, størrelsene og noen få
            ekstra valg. Deretter kan du sjekke mobilvisningen via QR-koden. En
            liten realistisk test er bedre enn å prøve å digitalisere alle
            varianter fra første dag.
          </p>
        </section>

        <section id="why" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hvorfor gir en digital meny mening for pizzeriaer?</h2>

            <p>
              Pizzeriaer har ofte en spesiell menystruktur. Én pizza kan tilbys
              i flere størrelser, topping kan legges til, noen ingredienser
              koster ekstra, og enkelte retter kan være tilgjengelige bare på
              bestemte tidspunkter.
            </p>

            <p>
              På papir eller i en PDF kan denne strukturen raskt bli
              uoversiktlig. I en digital meny kan kategorier, varianter og
              tilbud skilles tydeligere fra hverandre.
            </p>

            <h3>Digital struktur hjelper med pizzeriaens kompleksitet</h3>

            <p>
              En digital meny er ikke bare en moderne presentasjon. Den hjelper
              med å gjøre typisk pizzeria-kompleksitet enklere for gjester å
              forstå: størrelser, ekstra valg, topping, tilbud, vegetariske
              alternativer og tilgjengelighet.
            </p>

            <p>
              Den hjelper også pizzeriaen med å holde priser og tilbud oppdatert
              uten hele tiden å bytte filer, flyere eller trykte menykort.
            </p>
          </div>
        </section>

        <section id="structure" className="acquaintance">
          <h2>Hvordan bør en digital meny for en pizzeria struktureres?</h2>

          <p>
            En god pizzeria-meny trenger tydelige kategorier. Når pizza, pasta,
            salater, desserter, drikke, tilbud og ekstra valg ligger i én lang
            liste, mister gjestene raskt oversikten.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Klassiske pizzaer.</li>
            <li className="text__seo-li">Spesialpizzaer.</li>
            <li className="text__seo-li">Vegetarisk pizza.</li>
            <li className="text__seo-li">Pasta.</li>
            <li className="text__seo-li">Salater.</li>
            <li className="text__seo-li">Desserter.</li>
            <li className="text__seo-li">Drikke.</li>
            <li className="text__seo-li">Lunsjtilbud.</li>
          </ul>

          <p>
            Rekkefølgen betyr noe. Bestselgere og enkle kategorier bør være
            synlige først. Ekstra valg og spesialalternativer bør ikke forstyrre
            hovedflyten i menyen.
          </p>

          <VisualIdea>
            En smarttelefon med pizzeria-meny: klassiske pizzaer,
            spesialpizzaer, pasta, salater, drikke og en synlig seksjon for
            tilbud.
          </VisualIdea>
        </section>

        <section id="sizes" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hvordan bør pizzastørrelser, ekstra valg og topping vises?</h2>

            <p>
              Den største utfordringen i pizzeria-menyer er ofte varianter:
              liten, vanlig, stor, familiepizza, ekstra ost, ekstra topping,
              sterk variant, vegetarisk alternativ eller ingredienser som skal
              fjernes.
            </p>

            <p>
              Gjester bør forstå disse valgene raskt uten å måtte lese en
              komplisert pristabell.
            </p>

            <ol className="seoSteps">
              <li className="text__seo-li">
                <strong>Navngi størrelser tydelig:</strong> For eksempel 26 cm,
                32 cm eller familiepizza.
              </li>

              <li className="text__seo-li">
                <strong>Hold prislogikken enkel:</strong> Varianter bør ikke
                føles som skjulte kostnader.
              </li>

              <li className="text__seo-li">
                <strong>Forklar ekstra valg separat:</strong> Ekstra ost,
                grønnsaker, kjøtt eller saus bør være lett å forstå.
              </li>

              <li className="text__seo-li">
                <strong>Vis populære valg:</strong> For eksempel vegetarisk,
                sterk eller ekstra ost.
              </li>

              <li className="text__seo-li">
                <strong>Administrer tilgjengelighet:</strong> Hvis en topping
                ikke er tilgjengelig, bør den ikke vises fremtredende.
              </li>
            </ol>

            <h3>Jo flere varianter, desto tydeligere må strukturen være</h3>

            <p>
              Når én rett har mange mulige valg, bør ikke den digitale menyen
              skjule informasjonen i en forvirrende tekstblokk. Størrelser,
              ekstra valg og tilgjengelighet bør være enkle å skanne og forstå.
            </p>
          </div>
        </section>

        <section id="photos" className="acquaintance">
          <h2>Bør en pizzeria bruke bilder i den digitale menyen?</h2>

          <p>
            Bilder kan være svært nyttige for pizzeriaer, men ikke hver pizza
            trenger et bilde med en gang. Gode bilder er spesielt nyttige for
            spesialpizzaer, nye tilbud, desserter eller retter som gjester
            kanskje ikke kjenner godt.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Vis bestselgere.</li>
            <li className="text__seo-li">Fremhev spesialpizzaer.</li>
            <li className="text__seo-li">Presenter desserter.</li>
            <li className="text__seo-li">Gjør lunsjtilbud synlige.</li>
            <li className="text__seo-li">Vis realistiske porsjoner.</li>
            <li className="text__seo-li">Bruk konsekvent bildestil.</li>
            <li className="text__seo-li">Unngå veldig store filer.</li>
            <li className="text__seo-li">Bytt ut gamle bilder.</li>
          </ul>

          <p>
            For en pizzeria er et realistisk bilde ofte sterkere enn et perfekt
            reklamebilde. Gjester bør kunne forvente noe som ligner på det som
            senere kommer på tallerkenen eller i esken.
          </p>

          <VisualIdea>
            En digital pizzeria-meny med tre utvalgte bilder: en bestselger, en
            spesialpizza og en dessert. Bildene er lyse, realistiske og
            konsekvente.
          </VisualIdea>
        </section>

        <section id="mistakes" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hvilke feil skjer med digitale menyer for pizzeriaer?</h2>

            <p>
              Mange feil skjer når en digital meny behandles som en gammel
              flyer. En pizzeria trenger en tydelig digital struktur slik at
              varianter og ekstra valg forblir forståelige.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Vanlige feil</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Alle pizzaer vises i én lang liste.
                  </li>
                  <li className="text__seo-li">
                    Størrelser og priser er vanskelige å lese.
                  </li>
                  <li className="text__seo-li">
                    Ekstra valg er uklare eller forklares for sent.
                  </li>
                  <li className="text__seo-li">
                    Gamle flyer-PDF-er blir liggende online.
                  </li>
                  <li className="text__seo-li">
                    Utilgjengelig topping vises fortsatt.
                  </li>
                  <li className="text__seo-li">
                    Bilder passer ikke til den ekte pizzaen.
                  </li>
                  <li className="text__seo-li">
                    Lunsjtilbud blir ikke oppdatert.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Bedre</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">Bruk tydelige kategorier.</li>
                  <li className="text__seo-li">
                    Presenter størrelser logisk.
                  </li>
                  <li className="text__seo-li">
                    Forklar ekstra valg separat og tydelig.
                  </li>
                  <li className="text__seo-li">
                    Vedlikehold én sentral digital meny.
                  </li>
                  <li className="text__seo-li">
                    Skjul utilgjengelige ingredienser.
                  </li>
                  <li className="text__seo-li">Bruk realistiske bilder.</li>
                  <li className="text__seo-li">
                    Oppdater tilbud regelmessig.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="recommendation" className="acquaintance">
          <h2>Hvordan ville jeg startet som pizzeria-eier?</h2>

          <p>
            Jeg ville ikke prøvd å kartlegge hele menyen med alle varianter
            perfekt fra dag én. Jeg ville startet med kjernetilbudet og utvidet
            strukturen etterpå.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">
              <strong>Start med bestselgere:</strong> Legg inn 10 til 15
              populære pizzaer.
            </li>

            <li className="text__seo-li">
              <strong>Definer størrelser tydelig:</strong> For eksempel vanlig,
              stor og familiepizza.
            </li>

            <li className="text__seo-li">
              <strong>Begrens ekstra valg først:</strong> Legg bare til de
              viktigste ekstra valgene i starten.
            </li>

            <li className="text__seo-li">
              <strong>Test én tilbudskategori:</strong> For eksempel lunsjmeny
              eller familietilbud.
            </li>

            <li className="text__seo-li">
              <strong>Sjekk på smarttelefon:</strong> Gjester bør raskt forstå
              hva de kan velge.
            </li>

            <li className="text__seo-li">
              <strong>Utvid senere:</strong> Legg til pasta, salater, desserter
              og drikke etterpå.
            </li>
          </ol>

          <p>
            Slik holder den digitale menyen seg tydelig og kan vokse ut fra den
            virkelige restaurantflyten.
          </p>
        </section>

        <section id="next-steps" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hva bør du gjøre nå?</h2>

            <p>
              Hvis du vil lage en digital meny for pizzeriaen din, start med en
              liten realistisk test.
            </p>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">Opprett en digital meny.</li>
              <li className="text__seo-li">
                Legg til kategorier for pizza, pasta og drikke.
              </li>
              <li className="text__seo-li">
                Legg inn 10 til 15 populære pizzaer.
              </li>
              <li className="text__seo-li">
                Legg til størrelser og de viktigste ekstra valgene.
              </li>
              <li className="text__seo-li">
                Åpne menyen på en smarttelefon.
              </li>
              <li className="text__seo-li">
                Test QR-koden inne i restauranten.
              </li>
              <li className="text__seo-li">
                Utvid tilbud, bilder og flere kategorier etterpå.
              </li>
            </ul>

            <VisualIdea>
              En liten pizzeria-testmeny: 10 populære pizzaer, tydelige
              størrelser, noen få ekstra valg, én tilbudskategori og en
              smarttelefonvisning åpnet via QR-koden.
            </VisualIdea>
          </div>
        </section>

        <div id="bestplace" className="qrArticle__anchorTarget">
          <SeoWorkSteps lang="no" />
        </div>

        <section id="faq" className="acquaintance">
          <h2>FAQ om digitale menyer for pizzeriaer</h2>

          <div className="seoFaq">
            <details>
              <summary>Hva er en digital meny for pizzeriaer?</summary>
              <p>
                Det er en online-administrert meny for pizza, pasta, salater,
                drikke, tilbud, størrelser og ekstra valg som gjester kan åpne
                med QR-kode.
              </p>
            </details>

            <details>
              <summary>Kan jeg vise pizzastørrelser?</summary>
              <p>
                Ja. Størrelser som liten, vanlig, stor eller familiepizza kan
                vises tydelig med tilhørende priser.
              </p>
            </details>

            <details>
              <summary>Kan jeg vise ekstra valg og topping?</summary>
              <p>
                Ja. Ekstra valg bør vises tydelig og ikke bli for kompliserte,
                slik at gjestene forstår hva som er mulig.
              </p>
            </details>

            <details>
              <summary>
                Må jeg trykke QR-koden på nytt når prisene endres?
              </summary>
              <p>
                Nei, ikke hvis QR-koden peker til en stabil menylenke. Priser kan
                oppdateres online mens QR-koden forblir den samme.
              </p>
            </details>

            <details>
              <summary>Bør hver pizza ha et bilde?</summary>
              <p>
                Ikke nødvendigvis. Som første steg er bilder av bestselgere,
                spesialpizzaer, tilbud eller nye retter nok.
              </p>
            </details>

            <details>
              <summary>Kan jeg starte gratis?</summary>
              <p>
                Ja. For en første test er noen populære pizzaer, størrelser,
                priser og en QR-kode-test på smarttelefon nok.
              </p>
            </details>
          </div>
        </section>

        <SeoRelatedLinks
          lang="no"
          group="menu"
          exclude="/no/digital-meny-pizzeria"
        />
      </article>
    </main>
  );
}